import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const MAX = 15 * 1024 * 1024;
const button = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 disabled:opacity-50";
const primary = "rounded-lg bg-[#0b2545] px-4 py-2 text-sm text-white disabled:opacity-50";
const errorText = (e) => apiError(e.response?.data?.detail || e.message);

function ScanEditor({ onSave, onClose, busy }) {
  const [pages, setPages] = useState([]);
  const [index, setIndex] = useState(0);
  const [source, setSource] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState([0, 0, 0, 0]);
  const [name, setName] = useState("Scan");
  const canvas = useRef(null);
  const input = useRef(null);
  const camera = useRef(null);

  useEffect(() => {
    setRotation(0);
    setCrop([0, 0, 0, 0]);
    setSource(null);
    const file = pages[index];
    if (!file) return;
    let alive = true;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { if (alive) setSource(image); };
    image.onerror = () => toast.error("Use JPEG, PNG or WebP for scanning.");
    image.src = url;
    return () => { alive = false; URL.revokeObjectURL(url); };
  }, [pages, index]);

  useEffect(() => {
    if (!source || !canvas.current) return;
    const rotated = document.createElement("canvas");
    const scale = Math.min(1, 2400 / Math.max(source.width, source.height));
    const width = Math.round(source.width * scale);
    const height = Math.round(source.height * scale);
    const sideways = rotation % 180 !== 0;
    rotated.width = sideways ? height : width;
    rotated.height = sideways ? width : height;
    const context = rotated.getContext("2d");
    context.fillStyle = "white";
    context.fillRect(0, 0, rotated.width, rotated.height);
    context.translate(rotated.width / 2, rotated.height / 2);
    context.rotate(rotation * Math.PI / 180);
    context.drawImage(source, -width / 2, -height / 2, width, height);
    const [left, top, right, bottom] = crop;
    const x = Math.round(rotated.width * left / 100);
    const y = Math.round(rotated.height * top / 100);
    const w = Math.max(1, Math.round(rotated.width * (100-left-right) / 100));
    const h = Math.max(1, Math.round(rotated.height * (100-top-bottom) / 100));
    canvas.current.width = w;
    canvas.current.height = h;
    canvas.current.getContext("2d").drawImage(rotated, x, y, w, h, 0, 0, w, h);
  }, [source, rotation, crop]);

  const add = (event) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = "";
    if (pages.length + incoming.length > 20) {
      toast.error("Maximum 20 pages per scan."); return;
    }
    if (incoming.some((file) => !/^image\/(jpeg|png|webp)$/.test(file.type))) {
      toast.error("Choose JPEG, PNG or WebP images."); return;
    }
    setPages((current) => [...current, ...incoming]);
  };

  const apply = () => new Promise((resolve, reject) => {
    if (!source || !canvas.current) { reject(new Error("Wait for the page to load.")); return; }
    canvas.current.toBlob((blob) => {
      if (!blob) { reject(new Error("Unable to process this page.")); return; }
      const file = new File([blob], `page-${index + 1}.jpg`, { type: "image/jpeg" });
      setPages((current) => current.map((item, i) => i === index ? file : item));
      resolve(file);
    }, "image/jpeg", 0.9);
  });

  const save = async () => {
    try {
      const current = await apply();
      await onSave(name, pages.map((item, i) => i === index ? current : item));
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-3 overflow-auto">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-5 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Scan to PDF</h2>
          <button className={button} disabled={busy} onClick={onClose}>Close</button>
        </div>
        <input className="w-full border rounded-lg p-2" aria-label="Scan name"
          value={name} onChange={(e) => setName(e.target.value)} placeholder="File name" />
        <div className="flex flex-wrap gap-2">
          <button className={primary} disabled={busy} onClick={() => camera.current.click()}>Camera / Add page</button>
          <button className={button} disabled={busy} onClick={() => input.current.click()}>Choose photos</button>
          <input ref={camera} type="file" accept="image/jpeg,image/png,image/webp"
            capture="environment" hidden onChange={add} />
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp"
            multiple hidden onChange={add} />
        </div>
        <p className="text-sm text-slate-500">
          Add pages in order. Crop or rotate a page, then press Apply changes before switching pages.
          Maximum 20 pages and 15 MB of images.
        </p>
        {!!pages.length && (
          <>
            <div className="flex flex-wrap gap-2">
              {pages.map((file, i) => (
                <button key={i} disabled={busy}
                  className={i === index ? primary : button}
                  onClick={() => setIndex(i)}>Page {i + 1}</button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-slate-100 p-3 rounded-lg flex justify-center items-center">
                <canvas ref={canvas} className="max-w-full max-h-[50vh] object-contain" />
              </div>
              <div className="space-y-4">
                <button className={button} disabled={busy || !source}
                  onClick={() => setRotation((value) => (value + 90) % 360)}>Rotate 90°</button>
                {["Left", "Top", "Right", "Bottom"].map((label, i) => (
                  <label key={label} className="block text-sm">
                    Crop {label}: {crop[i]}%
                    <input className="block w-full" type="range" min="0" max="40"
                      disabled={busy} value={crop[i]}
                      onChange={(e) => setCrop((old) => old.map(
                        (value, j) => i === j ? Number(e.target.value) : value
                      ))} />
                  </label>
                ))}
                <div className="flex flex-wrap gap-2">
                  <button className={button} disabled={busy || !source}
                    onClick={() => apply().then(() => toast.success("Page updated")).catch((e) => toast.error(e.message))}>
                    Apply changes
                  </button>
                  <button className={button} disabled={busy} onClick={() => {
                    setPages((old) => old.filter((_, i) => i !== index));
                    setIndex(0);
                  }}>Remove page</button>
                </div>
              </div>
            </div>
            <button className={primary} disabled={busy || !source || !name.trim()} onClick={save}>
              {busy ? "Saving…" : "Save all pages as PDF"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Documents() {
  const [folder, setFolder] = useState("");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [query, setQuery] = useState("");
  const [printSize, setPrintSize] = useState("a4");
  const upload = useRef(null);
  const sequence = useRef(0);

  const load = useCallback(async () => {
    const ticket = ++sequence.current;
    setLoading(true);
    try {
      const res = await api.get("/documents", { params: { folder_id: folder } });
      if (ticket === sequence.current) {
        setFolders(res.data.folders);
        setFiles(res.data.files);
      }
    } catch (e) {
      if (ticket === sequence.current) {
        setFiles([]);
        toast.error(errorText(e));
      }
    } finally {
      if (ticket === sequence.current) setLoading(false);
    }
  }, [folder]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const action = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const createFolder = () => {
    const name = window.prompt("New folder name");
    if (name?.trim()) action(async () => {
      await api.post("/documents/folders", { name: name.trim() });
      toast.success("Folder created");
    });
  };

  const uploadFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    if (selected.some((file) => file.size > MAX || file.size === 0)) {
      toast.error("Choose non-empty files up to 15 MB each."); return;
    }
    await action(async () => {
      let count = 0;
      for (const file of selected) {
        const data = new FormData();
        data.append("file", file);
        data.append("folder_id", folder);
        try {
          await api.post("/documents/upload", data);
          count += 1;
        } catch (e) {
          throw new Error(`${count} file(s) saved. ${file.name}: ${errorText(e)}`);
        }
      }
      toast.success(`${count} file(s) uploaded`);
    });
  };

  const saveScan = async (name, pages) => {
    if (pages.reduce((total, file) => total + file.size, 0) > MAX) {
      throw new Error("Scan images together must be under 15 MB. Use fewer pages.");
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("name", name);
      data.append("folder_id", folder);
      pages.forEach((file) => data.append("pages", file));
      await api.post("/documents/scan", data);
      setScan(false);
      toast.success("Scan saved as PDF");
      await load();
    } finally { setBusy(false); }
  };

  const fetchBlob = async (file) => {
    const res = await api.get(`/documents/files/${file.id}/content`, {
      responseType: "blob",
    });
    return new Blob([res.data], { type: file.content_type });
  };

  const printable = (file) => file.content_type === "application/pdf" ||
    /^image\/(jpeg|png|webp)$/.test(file.content_type);

  const openFile = async (file) => {
    if (!printable(file)) {
      toast.info("Download this file and open it in its own app. For in-app printing, upload a PDF.");
      return;
    }
    setBusy(true);
    try {
      const blob = await fetchBlob(file);
      setPreview({ ...file, url: URL.createObjectURL(blob) });
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const download = async (file) => {
    setBusy(true);
    try {
      const blob = await fetchBlob(file);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const printFile = async (file) => {
    if (!printable(file)) {
      toast.info("Download and print from its own app, or upload a PDF version.");
      return;
    }
    const popup = window.open("", "_blank");
    if (!popup) { toast.error("Allow pop-ups to print this file."); return; }
    popup.document.title = file.name;
    popup.document.body.textContent = "Loading print preview…";
    try {
      const blob = await fetchBlob(file);
      const url = URL.createObjectURL(blob);
      popup.document.body.textContent = "";
      if (file.content_type === "application/pdf") {
        const note = popup.document.createElement("p");
        note.textContent = "Use the PDF viewer's Print button. On iPhone, use Share → Print.";
        popup.document.body.appendChild(note);
        const link = popup.document.createElement("a");
        link.href = url;
        link.textContent = "Open PDF";
        popup.document.body.appendChild(link);
        const frame = popup.document.createElement("iframe");
        frame.src = url;
        frame.title = file.name;
        frame.style.cssText = "width:100%;height:88vh;border:0";
        popup.document.body.appendChild(frame);
      } else {
        const style = popup.document.createElement("style");
        const sizing = printSize === "passport"
          ? "width:35mm;height:45mm;object-fit:contain"
          : printSize === "original"
          ? "max-width:190mm;max-height:270mm;object-fit:contain"
          : "width:190mm;height:265mm;object-fit:contain";
        style.textContent = `
          @page {size:A4;margin:10mm}
          body {margin:0;font-family:Arial,sans-serif}
          img {display:block;${sizing}}
          button {margin:12px;padding:10px 20px}
          @media print {button {display:none}}
        `;
        popup.document.head.appendChild(style);
        const control = popup.document.createElement("button");
        control.textContent = "Print photo";
        control.onclick = () => popup.print();
        popup.document.body.appendChild(control);
        const image = popup.document.createElement("img");
        image.alt = file.name;
        image.onload = () => { popup.focus(); popup.print(); };
        image.src = url;
        popup.document.body.appendChild(image);
      }
      // Keep the blob available while its print window remains open.
      const timer = setInterval(() => {
        if (popup.closed) {
          URL.revokeObjectURL(url);
          clearInterval(timer);
        }
      }, 2000);
    } catch (e) {
      popup.close();
      toast.error(errorText(e));
    }
  };

  const deleteFile = (file) => {
    if (!window.confirm(`Move "${file.name}" to Recycle Bin?`)) return;
    action(async () => {
      await api.delete(`/documents/files/${file.id}`);
      toast.success("Moved to Recycle Bin");
    });
  };

  const activeFolder = folders.find((item) => item.id === folder);
  const visible = files.filter((file) => file.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-navy">Documents & Folders</h1>
          <p className="text-sm text-slate-500">Photos, documents and scanned PDFs</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={button} disabled={busy} onClick={createFolder}>New folder</button>
          <button className={primary} disabled={busy} onClick={() => upload.current.click()}>Upload files</button>
          <button className={primary} disabled={busy} onClick={() => setScan(true)}>Scan to PDF</button>
          <input ref={upload} type="file" multiple hidden onChange={uploadFiles} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[230px_1fr] gap-5">
        <div className="bg-white border rounded-xl p-3 space-y-2 self-start">
          <button disabled={busy} className={`w-full text-left ${folder === "" ? primary : button}`}
            onClick={() => { setFolder(""); setQuery(""); }}>Main folder</button>
          {folders.map((item) => (
            <button key={item.id} disabled={busy}
              className={`w-full text-left break-words ${folder === item.id ? primary : button}`}
              onClick={() => { setFolder(item.id); setQuery(""); }}>
              📁 {item.name}
            </button>
          ))}
          <button className={`${button} w-full`} disabled={busy || loading} onClick={load}>Refresh</button>
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-4 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold mr-auto">{activeFolder?.name || "Main folder"}</h2>
            {activeFolder && <>
              <button className={button} disabled={busy} onClick={() => {
                const name = window.prompt("Folder name", activeFolder.name);
                if (name?.trim()) action(() => api.put(
                  `/documents/folders/${folder}`, { name: name.trim() }
                ));
              }}>Rename folder</button>
              <button className={button} disabled={busy} onClick={() => {
                if (window.confirm("Move this empty folder to Recycle Bin?")) {
                  action(async () => {
                    await api.delete(`/documents/folders/${folder}`);
                    setFolder("");
                  });
                }
              }}>Delete folder</button>
            </>}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <input className="border rounded-lg p-2 flex-1 min-w-0" placeholder="Search files in this folder"
              aria-label="Search files" value={query} onChange={(e) => setQuery(e.target.value)} />
            <label className="text-sm">Photo print size{" "}
              <select className="border p-2 rounded-lg" value={printSize}
                onChange={(e) => setPrintSize(e.target.value)}>
                <option value="a4">Fit to A4</option>
                <option value="passport">Passport 35 × 45 mm</option>
                <option value="original">Original size / fit if larger</option>
              </select>
            </label>
          </div>
          {busy && <p className="text-sm text-slate-500">Working…</p>}
          {loading ? <p className="py-10 text-center">Loading…</p> :
            visible.length === 0 ? <p className="py-10 text-center text-slate-500">No files in this folder.</p> :
            <div className="space-y-3">
              {visible.map((file, i) => (
                <div key={file.id} className="border rounded-lg p-3 flex flex-wrap gap-3 items-center">
                  <div className="min-w-0 flex-1">
                    <button className="text-left font-medium text-navy break-all"
                      disabled={busy} onClick={() => openFile(file)}>{i + 1}. {file.name}</button>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB ·{" "}
                      {new Date(file.created_at).toLocaleDateString("en-IN")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {printable(file) && <>
                      <button className={button} disabled={busy} onClick={() => openFile(file)}>Preview</button>
                      <button className={button} disabled={busy} onClick={() => printFile(file)}>Print</button>
                    </>}
                    <button className={button} disabled={busy} onClick={() => download(file)}>Download</button>
                    <button className={button} disabled={busy} onClick={() => setEditing({ ...file })}>Rename / Move</button>
                    <button className={button} disabled={busy} onClick={() => deleteFile(file)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>}
          <p className="text-xs text-slate-500">
            Maximum 15 MB per file. Photos and PDFs support preview and printing.
            Download other formats to open or print in their own app.
          </p>
        </div>
      </div>

      {scan && <ScanEditor busy={busy} onSave={saveScan} onClose={() => setScan(false)} />}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex justify-center items-center">
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4">
            <h2 className="font-semibold">Rename / Move file</h2>
            <input className="border rounded-lg p-2 w-full" aria-label="File name"
              value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <select className="border rounded-lg p-2 w-full" aria-label="Destination folder"
              value={editing.folder_id} onChange={(e) => setEditing({ ...editing, folder_id: e.target.value })}>
              <option value="">Main folder</option>
              {folders.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <div className="flex gap-2 justify-end">
              <button className={button} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
              <button className={primary} disabled={busy || !editing.name.trim()}
                onClick={() => action(async () => {
                  await api.put(`/documents/files/${editing.id}`, {
                    name: editing.name.trim(), folder_id: editing.folder_id,
                  });
                  setEditing(null);
                  toast.success("File updated");
                })}>Save</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 p-3 flex flex-col">
          <div className="bg-white rounded-t-xl p-3 flex gap-2 items-center">
            <span className="font-medium flex-1 truncate">{preview.name}</span>
            <button className={button} onClick={() => printFile(preview)}>Print</button>
            <button className={button} onClick={() => download(preview)}>Download</button>
            <button className={button} onClick={() => setPreview(null)}>Close</button>
          </div>
          <div className="bg-slate-100 flex-1 min-h-0 rounded-b-xl overflow-auto">
            {preview.content_type === "application/pdf" ?
              <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" /> :
              <img src={preview.url} alt={preview.name} className="max-w-full max-h-full mx-auto object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}
